import React, { useState } from 'react';
import { Send, X, Info, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const hrDomains = [
  { value: 'recruitment', label: 'Recruitment' },
  { value: 'learning', label: 'Learning & Development' },
  { value: 'talent', label: 'Talent Management' },
  { value: 'performance', label: 'Performance Management' },
  { value: 'wellbeing', label: 'Wellbeing & Engagement' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'hr-operations', label: 'HR Operations' },
  { value: 'compensation', label: 'Compensation & Benefits' },
  { value: 'workforce-planning', label: 'Workforce Planning' },
  { value: 'other', label: 'Other' },
];

const SubmitUseCaseModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    problemDescription: '',
    hrDomain: '',
    expectedImpact: '',
    goals: '',
    datasetInfo: '',
    name: '',
    email: '',
    companyName: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.problemDescription.trim()) newErrors.problemDescription = 'Problem description is required';
    if (!formData.hrDomain) newErrors.hrDomain = 'HR Domain is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    
    // Simulate API call - in production this would send to backend
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      problemDescription: '',
      hrDomain: '',
      expectedImpact: '',
      goals: '',
      datasetInfo: '',
      name: '',
      email: '',
      companyName: '',
    });
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Use Case Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for sharing your AI use case idea. Our team will review it and get back to you within 48 hours.
            </p>
            <Button onClick={handleClose} className="bg-blue-500 hover:bg-blue-600">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <Send className="h-6 w-6 text-blue-500" />
            Submit Your AI Use Case
          </DialogTitle>
        </DialogHeader>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3 mb-4">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            Do you have a concrete HR challenge where AI can help? Share your idea with us!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="E.g. Automated CV screening"
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>

          {/* Problem Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Problem Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.problemDescription}
              onChange={(e) => setFormData({ ...formData, problemDescription: e.target.value })}
              placeholder="Describe the current challenge or inefficiency in your HR process..."
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none ${
                errors.problemDescription ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.problemDescription && <p className="text-red-500 text-xs mt-1">{errors.problemDescription}</p>}
          </div>

          {/* HR Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HR Domain <span className="text-red-500">*</span>
            </label>
            <Select
              value={formData.hrDomain}
              onValueChange={(value) => setFormData({ ...formData, hrDomain: value })}
            >
              <SelectTrigger className={errors.hrDomain ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecteer..." />
              </SelectTrigger>
              <SelectContent>
                {hrDomains.map((domain) => (
                  <SelectItem key={domain.value} value={domain.value}>
                    {domain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.hrDomain && <p className="text-red-500 text-xs mt-1">{errors.hrDomain}</p>}
          </div>

          {/* Expected Impact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expected impact
            </label>
            <textarea
              value={formData.expectedImpact}
              onChange={(e) => setFormData({ ...formData, expectedImpact: e.target.value })}
              placeholder="What do you expect to achieve? (time savings, quality improvement, cost savings...)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goals
            </label>
            <textarea
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              placeholder="What do you want to achieve with this AI application?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>

          {/* Dataset Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dataset information
            </label>
            <textarea
              value={formData.datasetInfo}
              onChange={(e) => setFormData({ ...formData, datasetInfo: e.target.value })}
              placeholder="What data is available? (optional)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>

          {/* Personal Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Uw naam <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company name
            </label>
            <Input
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 py-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 py-6 bg-blue-500 hover:bg-blue-600"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitUseCaseModal;
